import { Button, Heading, IconButton } from '@assembly-js/design-system';
import MUIDialog from '@mui/material/Dialog';
import { ReactNode, useId } from 'react';

import introBackground from './assets/intro-bg.png';

const MODAL_WIDTH_PX = 800;
// On phones the modal caps here so it keeps a gutter on small screens.
const MOBILE_MAX_WIDTH_PX = 440;
const PAPER_SHADOW =
  '0 8px 24px rgba(14, 14, 16, 0.12), 0 2px 4px rgba(14, 14, 16, 0.06)';
const BACKDROP_COLOR = 'rgba(0, 0, 0, 0.3)';

interface IntroModalProps {
  open: boolean;
  /** Close button, backdrop click or escape. */
  onClose: () => void;
  /** Omit when the CTA already closes the modal, so the X is not a second copy of it. */
  closeLabel?: string;
  title: string;
  lines: string[];
  ctaLabel: string;
  onCta: () => void;
  /** Art laid over the dark panel, positioned by the caller. */
  visual: ReactNode;
}

/**
 * Onboarding intro shell: copy and one CTA beside a dark preview panel on
 * desktop, with the panel stacked above the copy on phones.
 */
export const IntroModal = ({
  open,
  onClose,
  closeLabel,
  title,
  lines,
  ctaLabel,
  onCta,
  visual,
}: IntroModalProps) => {
  const titleId = useId();

  return (
    <MUIDialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth={false}
      slotProps={{ backdrop: { sx: { backgroundColor: BACKDROP_COLOR } } }}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: MOBILE_MAX_WIDTH_PX, sm: MODAL_WIDTH_PX },
          m: 2,
          borderRadius: '8px',
          boxShadow: PAPER_SHADOW,
          overflowX: 'hidden',
        },
      }}
    >
      <div className="grid sm:grid-cols-2">
        <div
          className="relative min-h-[220px] overflow-hidden bg-[#0F0F10] bg-cover bg-center sm:order-last sm:min-h-[400px]"
          style={{ backgroundImage: `url(${introBackground})` }}
        >
          {visual}
          {closeLabel && (
            <IconButton
              icon="Close"
              variant="minimal"
              size="sm"
              aria-label={closeLabel}
              onClick={onClose}
              className="absolute right-3 top-3 z-10 size-8! rounded-full! border-0! bg-white/80! p-0! text-black-heading hover:bg-white! [&_svg]:size-3!"
            />
          )}
        </div>
        <div className="flex flex-col p-6 sm:p-8">
          <Heading
            tag="h2"
            size="lg"
            className="m-0! text-[20px]! font-normal! leading-7! text-black-heading"
          >
            <span id={titleId}>{title}</span>
          </Heading>
          <ul className="m-0 mt-5 flex list-disc flex-col gap-3 pl-5 text-[14px] leading-5 text-gray-small sm:mt-6">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col sm:mt-auto sm:pt-8">
            <Button
              variant="primary"
              className="h-9 w-full"
              label={ctaLabel}
              onClick={onCta}
            />
          </div>
        </div>
      </div>
    </MUIDialog>
  );
};
